
import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { realtimeCentral } from '@/core/RealtimeCentral';
import { debugLogger } from '@/utils/debugLogger';

// ISOLATED REALTIME INITIALIZATION HOOK
// This hook ensures realtime is only initialized AFTER auth is stable
export const useRealtimeInit = () => {
  const { user, loading } = useAuth();
  const initializationRef = useRef(false);

  useEffect(() => {
    // Only initialize realtime when:
    // 1. User is authenticated
    // 2. Auth is not loading
    // 3. Realtime is not already active
    // 4. We haven't already tried to initialize
    if (user && !loading && !realtimeCentral.isActive() && !initializationRef.current) {
      debugLogger.debug('useRealtimeInit: Initializing realtime for user:', user.id);
      initializationRef.current = true;
      
      // Small delay to ensure auth is completely stable
      const timer = setTimeout(() => {
        try {
          realtimeCentral.initialize(user.id);
        } catch (error) {
          debugLogger.error('useRealtimeInit: Error initializing realtime:', error);
          initializationRef.current = false;
        }
      }, 500); // Increased delay for stability

      return () => {
        clearTimeout(timer);
      };
    }
    
    // Cleanup realtime when user logs out
    if (!user && !loading && realtimeCentral.isActive()) {
      debugLogger.debug('useRealtimeInit: Cleaning up realtime - user logged out');
      realtimeCentral.cleanup();
      initializationRef.current = false;
    }
  }, [user, loading]);
};
